import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth APIs
export const authAPI = {
  sendOTP: (mobile) => api.post('/auth/send-otp', { mobile }),
  verifyOTP: (mobile, otp) => api.post('/auth/verify-otp', { mobile, otp }),
  register: (userData) => api.post('/auth/register', userData),
};

// Entity APIs
export const entityAPI = {
  getAll: () => api.get('/entities'),
  create: (data) => api.post('/entities', data),
  update: (id, data) => api.put(`/entities/${id}`, data),
  delete: (id) => api.delete(`/entities/${id}`),
};

// Food Item APIs
export const foodItemAPI = {
  getAll: (params = {}) => api.get('/food-items', { params }),
  getById: (id) => api.get(`/food-items/${id}`),
  create: (data) => api.post('/food-items', data),
  update: (id, data) => api.put(`/food-items/${id}`, data),
  delete: (id) => api.delete(`/food-items/${id}`),
  search: (entityId, query) => api.get('/food-items', { params: { entity_id: entityId, search: query } }),
};

// Order APIs
export const orderAPI = {
  create: (data, buyerId) => api.post(`/orders?buyer_id=${buyerId}`, data),
  getAll: (userId, role) => api.get('/orders', { params: { user_id: userId, role } }),
  updateStatus: (orderId, status, userId) => 
    api.put(`/orders/${orderId}/status?status=${status}&user_id=${userId}`),
};

// User APIs
export const userAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, { params: data }),
  delete: (id) => api.delete(`/users/${id}`),
  getSellers: (entityId) => api.get('/sellers', { params: { entity_id: entityId } }),
  // Profile endpoints
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
  updateProfile: (userId, profileData) => api.put(`/users/${userId}/profile`, profileData),
};

// Notification APIs
export const notificationAPI = {
  getAll: (userId) => api.get(`/notifications/${userId}`),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: (userId) => api.put(`/notifications/${userId}/read-all`),
};

// Recommendation APIs
export const recommendationAPI = {
  get: (userId, context) => api.post('/recommendations', { user_id: userId, context }),
};

// Analytics APIs
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getEntities: () => api.get('/analytics/entities'),
  getSunburst: () => api.get('/analytics/sunburst'),
};

// Buyer Insights APIs
export const buyerInsightsAPI = {
  getInsights: (buyerId) => api.get(`/buyers/${buyerId}/insights`),
};

// Dish Request APIs
export const dishRequestAPI = {
  create: (data, buyerId) => api.post(`/dish-requests?buyer_id=${buyerId}`, data),
  getAll: (userId, role, entityId) => api.get('/dish-requests', { 
    params: { user_id: userId, role, entity_id: entityId } 
  }),
  accept: (requestId, sellerId) => api.post(`/dish-requests/${requestId}/accept?seller_id=${sellerId}`),
  cancel: (requestId, buyerId) => api.delete(`/dish-requests/${requestId}?buyer_id=${buyerId}`),
};

// Surprise Me API
export const surpriseAPI = {
  getSurpriseItem: (entityId) => api.get('/food-items/surprise', { params: { entity_id: entityId } }),
};

// Poll APIs
export const pollAPI = {
  create: (data, userId) => api.post(`/polls?user_id=${userId}`, data),
  getAll: (entityId, userId) => api.get('/polls', { params: { entity_id: entityId, user_id: userId } }),
  vote: (pollId, userId) => api.post(`/polls/${pollId}/vote?user_id=${userId}`),
  removeVote: (pollId, userId) => api.delete(`/polls/${pollId}/vote?user_id=${userId}`),
};

// Admin Settings APIs
export const settingsAPI = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
};

// Bazaar Category APIs (Admin)
export const bazaarCategoryAPI = {
  getAll: (activeOnly = true) => api.get('/bazaar/categories', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/bazaar/categories', data),
  update: (id, data) => api.put(`/bazaar/categories/${id}`, data),
  delete: (id) => api.delete(`/bazaar/categories/${id}`),
};

// Bazaar Listing APIs
export const bazaarListingAPI = {
  getAll: (params = {}) => api.get('/bazaar/listings', { params }),
  getById: (id) => api.get(`/bazaar/listings/${id}`),
  create: (data, userId) => api.post(`/bazaar/listings?user_id=${userId}`, data),
  updateStatus: (id, status, userId) => api.put(`/bazaar/listings/${id}/status?status=${status}&user_id=${userId}`),
  delete: (id, userId) => api.delete(`/bazaar/listings/${id}?user_id=${userId}`),
  getMyListings: (userId) => api.get(`/bazaar/my-listings?user_id=${userId}`),
};

// Image Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getImageUrl: (filename) => `${API_BASE}/uploads/${filename}`,
};

// FAQ API (Cravy Chatbot)
export const faqAPI = {
  getAll: (category = null, activeOnly = true) => {
    const params = { active_only: activeOnly };
    if (category) params.category = category;
    return api.get('/faqs', { params });
  },
  getById: (id) => api.get(`/faqs/${id}`),
  create: (data) => api.post('/faqs', data),
  update: (id, data) => api.put(`/faqs/${id}`, data),
  delete: (id) => api.delete(`/faqs/${id}`),
  seed: () => api.post('/faqs/seed'),
};

export default api;
